import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, Gift } from 'lucide-react';
import type { JoinRequest, JoinResponse, MeResponse, PublicBattleInfo } from '@shared/types';
import { isValidCode } from '@shared/codes';
import { api, ApiError } from '@/lib/api';
import {
  clearDraft, clearMe, clearSession, loadDraft, loadMe, loadRef, refFromUrl, saveDraft, saveMe, saveRef, saveSession, sessionFor,
  type Remembered, type Session,
} from '@/lib/session';
import { isSeatMoved } from '@/lib/seat';
import { proofSession, signInWithAddress } from '@/lib/proof';
import { getStaffSession } from '@/staff/session';
import { useGame } from '@/store/game';
import { sfx } from '@/audio/sfx';
import { haptic } from '@/lib/haptics';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { Wordmark } from '@/brand/Wordmark';
import { Pulse } from '@/brand/Pulse';
import { ConfettiBurst } from '@/motion';
import { OfflineBanner } from '@/components/OfflineBanner';
import { Screen } from './parts/Screen';
import { COPY } from './parts/copy';
import { JOIN_CELEBRATION_MS, RESUME_PATIENCE_MS, type ResumeState } from './parts/moments';
import { useCalm } from './parts/time';
import { activePrize, prizePhrase, PRIVACY_PATH, termsPath } from './parts/prize';
import { useBattleLook } from '@/look';
import { withRef } from '@/components/Qr';
import { OpenInBrowser, useBrowserHint } from './parts/OpenInBrowser';
import { NEXT, NextBattle } from './parts/NextBattle';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * This screen's own words (the rest come from COPY). Each thing is said once: the field labels say what the name and email
 * are for, so the small print does not list them again; the battle card carries the code, so the code field waits behind
 * "Not this battle?"; rejoining with the same email is said where it matters, when a session could not be resumed.
 */
const JOIN = {
  otherCode: 'Not this battle? Enter a code',
  openBattles: 'Or join an open battle',
  // There is no result email: the address is the player's identity (the same email brings back their place) and how winners are reached.
  email: 'Email, to keep your place',
  privacy: 'MedSnapp Ltd, the data controller, uses your details to run the battle and to email you, and this device remembers them for next time. You can unsubscribe from the first email.',
  needName: 'Add a name to play.',
  network: 'Could not join. Check your signal and try again.',
  /** The toast after a resume or a rejoin: the lobby greets by name, so this only says nothing was lost. */
  back: 'Back where you left off.',
  waiting: (n: number) => (n > 0 ? `${n.toLocaleString('en-GB')} already in` : 'Be the first in'),
  playing: (n: number) => `${n.toLocaleString('en-GB')} playing`,
  resume: {
    expired: 'Your session has expired. Rejoin with the same email and your score comes back.',
    missing: 'We could not find you in this battle. Rejoin with the same email and your score comes back.',
    offline: 'We could not reach the battle. Check your signal, then rejoin with the same email: your score comes back.',
    // 9 Oct: another device took the seat (lib/seat); joining again takes it back once that device goes quiet, or with a sign-in.
    moved: 'Your seat moved to another device. Join again to carry on here.',
  },
  // 9 Oct: the seat is held for about a minute after its last activity, so a player whose browser died and who comes
  // back on a new device is turned away until then. A sign-in with the same address takes the seat back at once.
  seat: { before: "That email is playing on another device. If that's you, ", link: 'sign in', after: ' to carry on here, or try again in a minute.' },
};

/** The server's refusal while the seat is in use on another device (api/handlers/join.ts EMAIL_IN_USE). */
const SEAT_IN_USE = /already playing on another device/i;

const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/\.?$/, '.') : s);
type Mode = 'checking' | 'remembered' | 'form';
/** Inline links in small print: the padding gives a finger-sized target without moving the text. */
const LEGAL_LINK = 'py-3 font-semibold text-iv-strong underline underline-offset-2';

/**
 * The join screen, with remember-me (spec v2 section 2): reopening a battle's link resumes the game; remembered details
 * make the next battle one tap ("Join as Sam"); the form autosaves as you type; a failed resume never leaves an empty form.
 */
export default function Join() {
  const params = useParams(); const nav = useNavigate(); const location = useLocation();
  const reduce = useCalm();
  const urlCode = (params.code ?? '').toUpperCase();
  const handed = location.state as Partial<ResumeState> | null;
  const problem = handed?.resume;
  const [boot] = useState(() => {
    const remembered = loadMe(); const draft = loadDraft(); const stored = urlCode && !problem ? sessionFor(urlCode) : undefined;
    const prefill = draft && (!urlCode || !draft.code || draft.code.toUpperCase() === urlCode) ? draft : undefined;
    return { remembered, prefill, stored, storedName: urlCode ? sessionFor(urlCode)?.name : undefined };
  });
  const [remembered, setRemembered] = useState<Remembered | undefined>(boot.remembered);
  const [mode, setMode] = useState<Mode>(boot.stored ? 'checking' : boot.remembered ? 'remembered' : 'form');
  const [notice, setNotice] = useState<string | undefined>(problem ? JOIN.resume[problem] : undefined);
  const [code, setCode] = useState(urlCode || (boot.prefill?.code ?? '').toUpperCase());
  const [name, setName] = useState(boot.prefill?.name ?? boot.remembered?.name ?? boot.storedName ?? handed?.name ?? '');
  const [email, setEmail] = useState(boot.prefill?.email ?? boot.remembered?.email ?? '');
  // Consent is an act, never a default: the box starts unticked every time, whatever this device remembers (PECR).
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string>(); const [joined, setJoined] = useState(false);
  // With the seat-in-use refusal, where "sign in" goes: the sign-in page with the typed address, coming back to this battle's join page.
  const [seatSignIn, setSeatSignIn] = useState<string>();
  const fail = (text?: string, signIn?: string) => { setErr(text); setSeatSignIn(signIn); };
  const [info, setInfo] = useState<PublicBattleInfo>();
  // From a battle link the code is on the card; the field only opens if the player says it is the wrong battle.
  const [codeOpen, setCodeOpen] = useState(!urlCode);
  const codeInput = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false); const navTimer = useRef<number | undefined>(undefined);
  // Where the player came from (?ref= on the join link). Kept on the device for this battle, so a refresh, remember-me and
  // the game sending a player back to this page (whose link no longer carries it) all still credit the right source.
  const linkRef = useMemo(() => refFromUrl(), []);
  useEffect(() => { if (linkRef) saveRef(linkRef, urlCode); }, [linkRef, urlCode]);
  useBattleLook(info);
  // Inside Instagram: a short "open in your browser" hint; on a computer, a QR to play on a phone. Once per device.
  const hint = useBrowserHint();

  useEffect(() => () => { if (navTimer.current) window.clearTimeout(navTimer.current); }, []);

  function enterGame() {
    navTimer.current = window.setTimeout(() => nav('/play' + window.location.search, { replace: true }), reduce ? 120 : JOIN_CELEBRATION_MS);
  }

  // Reopening the join link with a stored session for this battle: check it and go straight back in.
  useEffect(() => {
    const stored = boot.stored; if (!stored) return;
    let live = true;
    const fallback = boot.remembered ? 'remembered' : 'form';
    const slow = window.setTimeout(() => { if (live) { setNotice(JOIN.resume.offline); setMode(fallback); } }, RESUME_PATIENCE_MS);
    api<MeResponse>('/me', { token: stored.token }).then((me) => {
      if (!live || busyRef.current) return;
      window.clearTimeout(slow);
      const session = { ...stored, name: me.name || stored.name };
      const g = useGame.getState(); g.reset();
      saveSession(session); g.setSession(session); g.setMe(me); g.applySnapshot(me.state);
      g.showToast(JOIN.back);
      nav('/play' + window.location.search, { replace: true });
    }).catch((e) => {
      if (!live) return;
      window.clearTimeout(slow);
      if (e instanceof ApiError && (e.status === 401 || e.status === 403 || e.status === 404)) { clearSession(); setNotice(JOIN.resume[isSeatMoved(e) ? 'moved' : e.status === 404 ? 'missing' : 'expired']); }
      else setNotice(JOIN.resume.offline);
      setMode(fallback);
    });
    return () => { live = false; window.clearTimeout(slow); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The form keeps a draft as you type, so an accidental refresh does not wipe it.
  useEffect(() => { if (mode === 'form' && !joined) saveDraft({ code, name, email, marketing: false }); }, [mode, code, name, email, joined]);

  // The battle behind the code: its title and whether it is live, so you know what you are joining.
  useEffect(() => {
    if (!isValidCode(code)) { setInfo(undefined); return; }
    let live = true; const c = code.toUpperCase();
    const t = window.setTimeout(() => {
      api<PublicBattleInfo>(`/battles/${c}/public`).then((i) => { if (live && i && typeof i.title === 'string') setInfo(i); }).catch(() => { if (live) setInfo(undefined); });
    }, c === urlCode ? 0 : 300);
    return () => { live = false; window.clearTimeout(t); };
  }, [code, urlCode]);

  async function join(d: { code: string; name: string; email: string }) {
    sfx.unlock(); // the join tap unlocks sound for the whole game
    const c = d.code.trim().toUpperCase(); const n = d.name.trim().replace(/\s+/g, ' '); const em = d.email.trim();
    if (!isValidCode(c)) { fail(COPY.join.badCode); return; }
    if (!n) { fail(JOIN.needName); return; }
    if (!EMAIL.test(em)) { fail(COPY.join.badEmail); return; }
    fail(undefined); setBusy(true); busyRef.current = true;
    const ref = linkRef ?? loadRef(c);
    try {
      // 18 Oct 2026 (MedSnapp): no opt-in box on the join form; players can unsubscribe from the first email.
      const body: JoinRequest = { name: n, email: em, marketing: false, ...(ref ? { ref } : {}) };
      // The session this device already holds for the battle, if any: it shows the server this is the same device, so
      // the seat comes straight back (another device with the same email is turned away while the seat is in use).
      const held = sessionFor(c);
      // 9 Oct: no session to resume, but a sign-in on this device for the typed address: its ID token proves the address
      // at join, so the saved details and the record come from the start (the server checks it; a mismatch is just a join).
      const proof = held ? undefined : proofSession(getStaffSession(), em.toLowerCase());
      const r = await api<JoinResponse>(`/battles/${c}/join`, { method: 'POST', body: JSON.stringify(body), ...(held ? { token: held.token } : proof ? { token: proof.token } : {}) });
      const session: Session = { code: c, token: r.token, playerId: r.playerId, name: n };
      const g = useGame.getState(); g.reset();
      saveSession(session); saveMe({ name: n, email: em, marketing: false }); clearDraft();
      if (ref) saveRef(ref, c);
      g.setSession(session); g.setJoined({ index: r.state.index, status: r.state.status, rejoined: Boolean(r.rejoined) }); g.applySnapshot(r.state);
      if (r.rejoined) g.showToast(JOIN.back);
      setJoined(true); sfx.play('ping'); haptic('join');
      enterGame();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && SEAT_IN_USE.test(e.message)) fail(JOIN.seat.before + JOIN.seat.link + JOIN.seat.after, signInWithAddress(em.toLowerCase(), `/j/${c}`));
      else fail(e instanceof ApiError ? sentence(e.message) : JOIN.network);
    } finally { setBusy(false); busyRef.current = false; }
  }

  function submit(e: FormEvent) { e.preventDefault(); void join({ code, name, email }); }
  function openCode() { setCodeOpen(true); fail(undefined); }
  // The field opens focused, with the link's code selected so typing replaces it.
  useEffect(() => { if (codeOpen && urlCode) { codeInput.current?.focus(); codeInput.current?.select(); } }, [codeOpen, urlCode]);
  function notYou() {
    clearMe(); clearDraft(); clearSession(); useGame.getState().reset();
    setRemembered(undefined); setName(''); setEmail(''); setNotice(undefined); fail(undefined); setMode('form');
  }

  // The refusal, once: the seat-in-use one carries its sign-in link in the middle of the sentence.
  const alert = err && (
    <p role="alert" className="rounded-tile bg-wrong-soft px-4 py-3 text-[15px] text-ink">
      {seatSignIn ? <>{JOIN.seat.before}<Link to={seatSignIn} className={LEGAL_LINK}>{JOIN.seat.link}</Link>{JOIN.seat.after}</> : err}
    </p>
  );
  const codeField = (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-semibold text-ink-2">{COPY.join.code}</span>
      <input id="code" ref={codeInput} className="rd-field font-display text-2xl font-extrabold uppercase tabular-nums tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 5))}
        autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false} inputMode="text" maxLength={5} aria-invalid={err === COPY.join.badCode} />
    </label>
  );
  const submitLabel = (label: string) => (
    <AnimatePresence mode="wait" initial={false}>
      {joined ? (
        <motion.span key="in" className="inline-flex items-center justify-center gap-2" initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.4, rotate: -20 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 560, damping: 18 }}>
          <Check aria-hidden="true" className="size-6" strokeWidth={3} />{COPY.join.joined}
        </motion.span>
      ) : (
        <motion.span key="label" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }} transition={{ duration: 0.12 }}>{busy ? COPY.join.busy : label}</motion.span>
      )}
    </AnimatePresence>
  );
  const finished = info && (info.status === 'ended' || info.status === 'podium');
  const prize = activePrize(info?.prize);
  const battleCode = (info?.code ?? code).toUpperCase();
  const showCodeField = codeOpen || !urlCode;
  // The code is on the card only while its field is closed, so it never shows twice.
  const cardCode = !showCodeField && isValidCode(battleCode) ? battleCode : undefined;
  const count = info ? (info.status === 'lobby' ? JOIN.waiting(info.playerCount) : finished ? undefined : JOIN.playing(info.playerCount)) : undefined;

  const card = (
    <section aria-labelledby={info ? 'battle-title' : undefined} aria-busy={mode === 'checking'} className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className={`px-5 pb-1.5 pt-3 transition-colors duration-300 ${joined ? 'bg-correct-soft text-correct' : 'bg-brand-tint text-brand'}`}>
        <Pulse mood={joined ? 'spike' : mode === 'checking' ? 'racing' : 'steady'} width="100%" height={34} />
      </div>
      <div className="flex flex-col gap-4 p-5">
        {(info || cardCode) && (
          <div className="flex flex-col gap-1.5 border-b border-line pb-4">
            {info && <h1 id="battle-title" className="font-display text-xl font-extrabold leading-tight text-ink">{info.title}</h1>}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-ink-2">
              {info && <Chip tone={finished ? 'slate' : info.status === 'lobby' ? 'brand' : 'rose'}>{COPY.status[info.status]}</Chip>}
              {count && <span>{count}</span>}
              {cardCode && (
                <span className="numeral ml-auto rounded-[8px] border border-line px-2 py-0.5 text-sm tracking-[0.16em] text-ink" aria-label={`Battle code ${cardCode.split('').join(' ')}`}>{cardCode}</span>
              )}
            </div>
            {prize && !finished && (
              // What can be won; its terms are linked once, in the small print under the card.
              <p className="mt-1 flex items-start gap-2 text-[15px] font-semibold leading-snug text-ink">
                <Gift aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-cart-deep" strokeWidth={2.4} />
                <span>{COPY.prize.win(prizePhrase(prize.prize))}</span>
              </p>
            )}
            {cardCode && mode !== 'checking' && (
              <button type="button" onClick={openCode} className="-mb-2 min-h-11 self-start text-sm font-semibold text-iv-strong underline-offset-4 hover:underline">{JOIN.otherCode}</button>
            )}
          </div>
        )}

        {mode === 'checking' ? (
          <p className="py-2 font-semibold text-ink-2">{COPY.join.checking}</p>
        ) : mode === 'remembered' && remembered ? (
          <>
            {showCodeField && codeField}
            {/* 13 Oct: the name stays editable here too, so a second player on this phone, or a new name for this battle, needs no detour. */}
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-ink-2">{COPY.join.name}</span>
              <input id="name" className="rd-field" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoComplete="nickname" required aria-invalid={err === JOIN.needName} />
            </label>
            <Button type="button" done={joined} disabled={busy} busy={busy} onClick={() => void join({ code, name, email: remembered.email })}>
              {submitLabel(name.trim() ? `Join as ${name.trim().replace(/\s+/g, ' ')}` : COPY.join.submit)}
            </Button>
            {/* The button says the name; the email it joins with is the one thing left to show. */}
            <p className="-mt-1 break-all text-center text-sm text-ink-3">{remembered.email}</p>
            {alert}
            <div className="flex items-center justify-center gap-6 text-sm font-semibold">
              <button type="button" onClick={notYou} className="min-h-11 px-1 text-iv-strong underline-offset-4 hover:underline">{COPY.join.notYou}</button>
              <button type="button" onClick={() => setMode('form')} className="min-h-11 px-1 text-ink-2 underline-offset-4 hover:underline">{COPY.join.edit}</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            {showCodeField && codeField}
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-ink-2">{COPY.join.name}</span>
              <input id="name" className="rd-field" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoComplete="nickname" required aria-invalid={err === JOIN.needName} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-ink-2">{JOIN.email}</span>
              <input id="email" className="rd-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" required aria-invalid={err === COPY.join.badEmail} />
            </label>
            {alert}
            <Button type="submit" done={joined} disabled={busy} busy={busy}>{submitLabel(COPY.join.submit)}</Button>
          </form>
        )}
      </div>
    </section>
  );

  return (
    // From a battle link the battle card leads, under a small wordmark; opened plainly, the page introduces Rounds first.
    // One Pulse per screen: it lives in the card's band, so the wordmark draws none. The offline line sits above the frame.
    <>
    <OfflineBanner />
    <Screen label="Join the battle" className="gap-6" header={urlCode ? (
      <header className="flex items-center"><Wordmark variant="compact" className="text-[1.0625rem]" /></header>
    ) : undefined}>
      {!urlCode && (
        <header className="flex flex-col gap-3 pt-6">
          <Wordmark variant="full" className="text-[2.5rem] leading-none" />
          <p className="text-lg leading-snug text-ink-2">{COPY.join.hero}</p>
        </header>
      )}

      {notice && <p role="status" className="rounded-tile border border-cart bg-cart-soft px-4 py-3 text-[15px] leading-snug text-ink">{notice}</p>}

      {(hint.kind === 'instagram' || hint.kind === 'tiktok') && !joined && <OpenInBrowser kind={hint.kind} os={hint.os} url={window.location.href} where="join" onClose={hint.dismiss} />}

      {/* 14 Oct: typed the address from a Live: the battle that is live or about to start, one tap away. */}
      {!urlCode && !joined && <NextBattle heading={(r) => (r.status === 'lobby' ? NEXT.soon : NEXT.live)} cta={NEXT.join} />}

      {card}

      {!urlCode && !joined && (
        <Link to="/open" className="-mt-2 inline-flex min-h-11 w-fit items-center self-center px-1 text-[15px] font-semibold text-iv-strong underline-offset-4 hover:underline">{JOIN.openBattles}</Link>
      )}

      {hint.kind === 'desktop' && !joined && (
        <OpenInBrowser kind="desktop" where="join" onClose={hint.dismiss}
          url={isValidCode(code) ? withRef(`${window.location.origin}/j/${code.toUpperCase()}`, linkRef ?? loadRef(code.toUpperCase())) : window.location.href} />
      )}

      <p className="text-xs leading-relaxed text-ink-3">
        {JOIN.privacy}{' '}
        <a className={LEGAL_LINK} href={PRIVACY_PATH} target="_blank" rel="noopener">{COPY.join.privacyLink}</a>
        {prize && isValidCode(battleCode) && (
          <>
            <span className="mx-1.5" aria-hidden="true">·</span>
            <a className={LEGAL_LINK} href={termsPath(battleCode)} target="_blank" rel="noopener">{COPY.join.termsLink}</a>
          </>
        )}
      </p>
      {joined && <ConfettiBurst trigger="joined" origin="top" count={36} />}
    </Screen>
    </>
  );
}
